import React, { ReactNode } from 'react';
import {
  InvoiceDisplay,
  type Invoice,
} from '../../pages/Settings/subpages/SettingsSubscription/SettingsCbsSubscription/InvoiceRenderer';

interface BeforeAfterState {
  currentLabel: string;
  newLabel: string;
  currentValue: string;
  newValue: string;
  changeDescription?: string;
}

interface InvoiceSection {
  immediateInvoice?: Invoice;
  recurringInvoice?: Invoice;
}

interface PreviewModalProps {
  title: string;
  children?: ReactNode;
  onCancel: () => void;
  onConfirm?: () => void;
  isProcessing?: boolean;
  confirmText?: string;
  cancelText?: string;
  confirmDisabled?: boolean;
  beforeAfterState?: BeforeAfterState;
  invoiceSection?: InvoiceSection;
  importantNotes?: string[];
}

const BeforeAfterStateDisplay = ({
  beforeAfterState,
}: {
  beforeAfterState: BeforeAfterState;
}) => {
  const { currentLabel, newLabel, currentValue, newValue, changeDescription } =
    beforeAfterState;

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
      <div className="text-blue-800">
        {/* Headers level */}
        <div className="flex items-center gap-4 mb-3">
          <div className="flex-1 text-center">
            <p className="text-sm font-medium text-blue-600">{currentLabel}</p>
          </div>
          <div className="flex-shrink-0">
            <span className="text-blue-600 text-lg opacity-0">→</span>
          </div>
          <div className="flex-1 text-center">
            <p className="text-sm font-medium text-blue-600">{newLabel}</p>
          </div>
        </div>
        {/* Values and arrow level */}
        <div className="flex items-center gap-4 mb-2">
          <div className="flex-1 text-center">
            <p className="text-lg font-semibold break-words">{currentValue}</p>
          </div>
          <div className="flex-shrink-0">
            <span className="text-blue-600 text-lg">→</span>
          </div>
          <div className="flex-1 text-center">
            <p className="text-lg font-semibold break-words">{newValue}</p>
          </div>
        </div>
        {changeDescription && (
          <div className="text-center">
            <span className="text-sm text-blue-600">{changeDescription}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const ImportantNotesDisplay = ({ notes }: { notes: string[] }) => {
  return (
    <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
      <h4 className="text-sm font-semibold text-yellow-800 mb-2">
        Important Notes
      </h4>
      <ul className="text-sm text-yellow-800 space-y-1">
        {notes.map((note, index) => (
          <li key={index}>• {note}</li>
        ))}
      </ul>
    </div>
  );
};

export const PreviewModal = ({
  title,
  children,
  onCancel,
  onConfirm,
  isProcessing = false,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmDisabled = false,
  beforeAfterState,
  invoiceSection,
  importantNotes,
}: PreviewModalProps) => {
  return (
    <div className="flex flex-col max-h-[80vh]">
      <h1 className="text-xl font-semibold text-gray-900 mb-4">{title}</h1>

      {/* Before/After State Display - Fixed Section */}
      {beforeAfterState && (
        <BeforeAfterStateDisplay beforeAfterState={beforeAfterState} />
      )}

      {/* Scrollable Content Section */}
      <div className="flex-1 overflow-y-auto mb-6">
        {children && <div className="mb-6">{children}</div>}

        {/* Invoice Information - Inside scrollable area */}
        {invoiceSection && (
          <div className="space-y-4">
            {invoiceSection.immediateInvoice && (
              <InvoiceDisplay
                invoice={invoiceSection.immediateInvoice}
                title="Immediate Charges (Due Today)"
              />
            )}
            {invoiceSection.recurringInvoice && (
              <InvoiceDisplay
                invoice={invoiceSection.recurringInvoice}
                title="Recurring Charges (Next Billing Cycle)"
              />
            )}
          </div>
        )}
      </div>

      {/* Important Notes - Always visible at bottom */}
      {importantNotes && importantNotes.length > 0 && (
        <div className="mb-6">
          <ImportantNotesDisplay notes={importantNotes} />
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 bg-white">
        <button
          onClick={onCancel}
          disabled={isProcessing}
          className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {cancelText}
        </button>
        {onConfirm && (
          <button
            onClick={onConfirm}
            disabled={isProcessing || confirmDisabled}
            className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
              isProcessing || confirmDisabled
                ? 'bg-gray-400'
                : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
            }`}
          >
            {isProcessing ? 'Processing...' : confirmText}
          </button>
        )}
      </div>
    </div>
  );
};

export default PreviewModal;
